/**
 * Onboarding Flow Type Definitions
 *
 * Centralized type definitions for the onboarding flow to ensure type safety
 * and eliminate unsafe `any` type assertions throughout the codebase.
 */

// API Error Types

/**
 * Standardized backend error object (matches BACKEND_STANDARDS Section 6)
 */
export interface StandardErrorObject {
  code: string;
  message: string;
  details?: unknown[];
  timestamp: string;
}

export interface ApiErrorData {
  success?: boolean;
  error?: string | StandardErrorObject;
  message?: string;
  code?: string;
  errors?: Record<string, string>;
  errorType?: string;
  details?: unknown;
  requestId?: string;
}

export interface ApiError {
  status?: number | string;
  originalStatus?: number;
  data?: ApiErrorData;
  message?: string;
  name?: string;
  error?: string;
}

export function isApiError(error: unknown): error is ApiError {
  return (
    typeof error === 'object' &&
    error !== null &&
    ('status' in error || 'data' in error || 'message' in error)
  );
}

import { getErrorMessage } from '@voley/shared-utils';

export function getApiErrorMessage(error: unknown): string {
  if (!isApiError(error)) {
    return getErrorMessage(error);
  }

  return (
    extractErrorMessage(error.data) ||
    error.message ||
    'An error occurred. Please try again.'
  );
}

/**
 * Extract error message string from API error data.
 * Handles both old format ({ error: "string" }) and new standardized format ({ error: { code, message } }).
 */
export function extractErrorMessage(errorData: ApiErrorData | undefined, fallback: string = ''): string {
  if (!errorData) return fallback;

  // New standardized format: { error: { code, message } }
  if (typeof errorData.error === 'object' && errorData.error?.message) {
    return errorData.error.message;
  }

  // Old format: { error: "string" } or { message: "string" }
  if (typeof errorData.error === 'string') return errorData.error;
  if (typeof errorData.message === 'string') return errorData.message;

  return fallback;
}

/**
 * Extract error code from API error data.
 * Handles both old format ({ code: "string" }) and new standardized format ({ error: { code } }).
 */
export function extractErrorCode(errorData: ApiErrorData | undefined): string | undefined {
  if (!errorData) return undefined;

  // New standardized format: { error: { code, message } }
  if (typeof errorData.error === 'object' && errorData.error?.code) {
    return errorData.error.code;
  }

  // Old format: { code: "string" }
  return errorData.code;
}

// Authentication & Session Types

export interface OnboardingSessionInfo {
  currentStep?: string;
  id?: string;
  status?: string;
  completedSteps?: string[];
}

export interface LoginResultUser {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  organizationId?: string;
}

export interface LoginResult {
  onboardingSession?: OnboardingSessionInfo;
  user?: LoginResultUser;
  stytchUserId?: string;
  setupCompleted?: boolean;
}

export interface SignupResult {
  user: {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
    organizationId: string;
  };
  organization: {
    id: string;
    name: string;
  };
  stytchUserId: string;
  setupCompleted: boolean;
}

// RTK Query Types

export interface MutationTrigger<T> {
  unwrap: () => Promise<T>;
  abort?: () => void;
}

export interface QueryResult<T> {
  data?: T;
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
  error?: ApiError;
}

// User Profile Types

export interface UserOrganization {
  id: string;
  name: string;
}

export interface UserProfile {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  organizationId: string;
  organization?: UserOrganization;
  setupCompleted?: boolean;
  isActive?: boolean;
  phoneNumber?: string;
  profileImageUrl?: string;
  stytchUserId?: string;
}

// Step Data Types

export interface CompanyInfoStepData {
  companySize?: string;
  clientCount?: string;
  industryType?: string;
  industryOther?: string;
  currentSoftware?: string[];
  softwareOther?: string;
  businessGoals?: string;
}

export interface PaymentSetupStepData {
  paymentMethodAdded?: boolean;
  selectedPackageIds?: string[];
  isConsented?: boolean;
  stripeCustomerId?: string;
  planSelected?: string;
  setupIntentId?: string;
}

export interface AccountCreationStepData {
  email?: string;
  firstName?: string;
  lastName?: string;
  phoneNumber?: string;
  organizationName?: string;
}

export interface DemoStepData {
  completedHotspots?: string[];
  completedAt?: string;
}

export type StepData =
  | CompanyInfoStepData
  | PaymentSetupStepData
  | AccountCreationStepData
  | DemoStepData
  | Record<string, unknown>;

// Session Storage Types

export interface CachedSessionData {
  email?: string;
  firstName?: string;
  lastName?: string;
  phoneNumber?: string;
  organizationName?: string;
  stytchUserId?: string;
  sessionToken?: string;
  sessionJwt?: string;
  cachedAt?: number;
}

// Onboarding Session Types

export type OnboardingStatus = 'in_progress' | 'completed' | 'abandoned';

export interface OnboardingSession {
  id: string;
  organizationId: string;
  userId: string;
  currentStep: string;
  status: OnboardingStatus;
  completedSteps: string[] | Record<string, { completedAt: string }>;
  stepData: Record<string, StepData>;
  progress?: number;
  messagingEnabled?: boolean;
  errorDetails?: string | null;
  lastActivityAt?: string;
  createdAt: string;
  updatedAt: string;
}

export interface OnboardingSessionResponse {
  data?: OnboardingSession;
}

// Navigation Types

export interface OnboardingNavigationState {
  isNavigating: boolean;
  navigationTarget: string | null;
}

export type StripeInitState = 'idle' | 'loading' | 'ready' | 'error';

// Form Types

export interface SignupFormData {
  workEmail: string;
  companyName: string;
  firstName: string;
  lastName: string;
  password: string;
  confirmPassword: string;
}

export interface CompanyInfoFormData {
  companySize: string;
  clientCount: string;
  industry: string;
  industryOther?: string;
  currentSoftware: string[];
  softwareOther?: string;
  businessGoals?: string;
}

// Timeout Wrapper Types

export interface CallWithTimeoutOptions {
  onTimeout?: () => void;
  signal?: AbortSignal;
}

// TimeoutError: use { TimeoutError } from '@voley/shared-utils/with-timeout' instead of a local copy.

// Package & Subscription Types

export interface Package {
  id: string;
  name: string;
  code: string;
  description?: string;
  price: number;
  interval: 'month' | 'year';
  features?: string[];
  is_active: boolean;
}

export interface SubscriptionResult {
  id: string;
  package_id: string;
  organization_id: string;
  status: string;
  started_at: string;
}
